/**
 * Login Page — For hotels. Lobby-themed.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Leaf } from 'lucide-react';
import { auth } from '@/lib/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password required'); return; }
    setLoading(true);
    try {
      const { data } = await auth.login({ email: email.trim(), password });
      localStorage.setItem('easystay_token', data.token);
      toast.success(`Welcome, ${data.user.name}`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="lobby-theme min-h-screen flex flex-col" data-testid="login-page">
      <div className="absolute inset-0 lobby-glow pointer-events-none" />
      <div className="lobby-grain" />

      {/* Minimal nav */}
      <nav className="relative z-10 border-b border-[color:var(--l-line)]">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 text-[color:var(--l-cream-2)] hover:text-[color:var(--l-cream)] transition-colors" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-[color:var(--l-copper)] flex items-center justify-center">
              <span className="l-display text-[color:var(--l-bg)] text-xs">b</span>
            </div>
            <span className="l-display text-[15px]">getbitsy</span>
          </Link>
        </div>
      </nav>

      {/* Sign-in panel */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-md lobby-reveal">
          <div className="text-[13px] tracking-wide text-[color:var(--l-cream-3)] mb-3">For hotels & operators</div>
          <h1 className="l-display text-4xl sm:text-5xl">
            Sign in to<br />
            your <span className="text-[color:var(--l-copper)]">front desk</span>.
          </h1>
          <p className="mt-4 text-[color:var(--l-cream-2)]">
            Manage bookings, pricing, hardware and agents — all in one place.
          </p>

          <form onSubmit={handleSubmit} className="mt-10 space-y-5" data-testid="login-card">
            <div>
              <label htmlFor="email" className="block text-[13px] text-[color:var(--l-cream-2)] mb-2">Email</label>
              <input
                id="email"
                type="email"
                placeholder="you@yourhotel.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="login-email"
                className="l-input w-full px-4 py-3.5 text-[15px] outline-none"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-[13px] text-[color:var(--l-cream-2)] mb-2">Password</label>
              <input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="login-password"
                className="l-input w-full px-4 py-3.5 text-[15px] outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="l-btn-copper h-12 w-full text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-[color:var(--l-line)] text-center">
            <Link to="/register" className="text-[14px] text-[color:var(--l-cream-2)] hover:text-[color:var(--l-copper)] transition-colors">
              New here? Register your hotel →
            </Link>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-[color:var(--l-line)] py-6">
        <div className="max-w-[1320px] mx-auto px-5 sm:px-8 flex items-center justify-between text-[12px] text-[color:var(--l-cream-3)]">
          <span>Powered by the HERMES Protocol.</span>
          <span className="inline-flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-[color:var(--l-copper)]" /> Hospitality, quietly rebuilt.
          </span>
        </div>
      </footer>
    </div>
  );
}
